import { createContext, useContext, useEffect, useState, useCallback } from "react";
import API from "@/lib/api";

// user states: null = checking, false = guest, object = authenticated
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await API.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await API.post("/auth/login", { email, password });
        setUser(data);
        return data;
    };

    // Registrazione: crea account in stato "email non verificata" e invia
    // l'email di conferma. NON effettua login automatico. Ritorna un oggetto
    // { ok, pending_verification, email, email_sent, verification_url? }.
    const register = async (payload) => {
        const { data } = await API.post("/auth/register", payload);
        return data;
    };

    const verifyEmail = async (token) => {
        const { data } = await API.post("/auth/verify-email", { token });
        setUser(data);
        return data;
    };

    const resendVerification = async (email) => {
        const { data } = await API.post("/auth/resend-verification", { email });
        return data;
    };

    const forgotPassword = async (email) => {
        const { data } = await API.post("/auth/forgot-password", { email });
        return data;
    };

    const resetPassword = async (token, new_password) => {
        const { data } = await API.post("/auth/reset-password", { token, new_password });
        if (data.user) setUser(data.user);
        return data;
    };

    const logout = async () => {
        try {
            await API.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setUser(false);
    };

    const updateProfile = async (payload) => {
        const { data } = await API.patch("/auth/me", payload);
        setUser(data);
        return data;
    };

    const changePassword = async (payload) => {
        await API.post("/auth/change-password", payload);
    };

    return (
        <AuthCtx.Provider
            value={{
                user,
                login,
                register,
                verifyEmail,
                resendVerification,
                forgotPassword,
                resetPassword,
                logout,
                refresh,
                updateProfile,
                changePassword,
            }}
        >
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
